import React from "react";

import { Button, SectionCard, Spinner } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import CompaniesService from "@/services/CompaniesService";
import ViewsService from "@/services/ViewsService";

import { EntityRow } from "@/components/molecules/EntityRow/EntityRow";
import { PlanGate } from "@/components/organisms/PlanGate/PlanGate";
import { PlanUsage } from "@/components/organisms/PlanGate/PlanUsage";

import { projectKeys } from "@/constants/CQueryKeys";

import { TDatesFilter } from "@/types";

import { PROJECT_LIST_METRICS } from "./ProjectsCard.columns";

/** Whose projects the card lists: a view's, or one client's. */
export type TProjectsScope = "views" | "clients";

export interface IProjectsCardProps {
	scope: TProjectsScope;
	/** The view or the client, per `scope`. */
	id: number;
	period: TDatesFilter;
	/** May this user add one here (role, lock)? The plan is checked inside. */
	canCreate: boolean;
	onCreate: () => void;
	className?: string;
}

/**
 * The projects a view gathers - or those done for one client - with
 * their month's figures, each row opening the project page. Keyed
 * under `["projects", …]` so the mutations that invalidate the
 * sidebar's list reach it too.
 */
export const ProjectsCard: React.FC<IProjectsCardProps> = ({
	scope,
	id,
	period,
	canCreate,
	onCreate,
	className,
}) => {
	const navigate = useNavigate();
	const ofClient = "clients" === scope;

	const { data, isLoading } = useQuery({
		queryKey: ofClient
			? projectKeys.ofClientSummary(id, period)
			: projectKeys.summary(id, period),
		queryFn: () =>
			ofClient
				? CompaniesService.fetchProjectsSummary(id, period)
				: ViewsService.getViewProjectsSummary(id, period),
	});
	const projects = [...(data ?? [])].sort((a, b) =>
		a.name.localeCompare(b.name)
	);

	return (
		<SectionCard
			data-testid="projects-card"
			title={
				<>
					{t("projects.title")}
					{data && (
						<span className="ml-1.5 font-normal text-neutral-500">
							· {projects.length}
						</span>
					)}
				</>
			}
			className={className}
			actions={
				canCreate && (
					<span className="flex items-center gap-3">
						<PlanUsage on="projects" />
						<PlanGate on="projects">
							<Button
								size="sm"
								color="primary"
								picto="add"
								onClick={onCreate}
							>
								{t("projects.new")}
							</Button>
						</PlanGate>
					</span>
				)
			}
		>
			{isLoading && <Spinner className="mx-auto my-6" />}
			{!isLoading && projects.length === 0 && (
				<div className="flex flex-col gap-1 py-4 text-sm">
					<span className="font-medium">
						{t(
							ofClient
								? "projects.emptyClientList"
								: "projects.emptyList"
						)}
					</span>
					<span className="text-neutral-500">
						{t("projects.emptyHint")}
					</span>
				</div>
			)}
			<div className="flex flex-1 flex-col divide-y divide-neutral-100 overflow-y-auto">
				{projects.map((project) => (
					<EntityRow
						key={project.id}
						className="px-1"
						data-testid={`project-row-${project.id}`}
						color={project.color}
						name={project.name}
						description={project.description}
						isArchived={project.isArchived}
						metrics={PROJECT_LIST_METRICS(project, {
							withClient: !ofClient,
						})}
						onClick={() => navigate(`/projects/${project.id}`)}
					/>
				))}
			</div>
		</SectionCard>
	);
};
